import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, MessageCircle } from "lucide-react";
import { SITE } from "@/data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[#0B162C]/95 backdrop-blur-md" : "bg-[#0B162C]"}`}
    >
      {/* Top strip */}
      <div className="hidden md:block border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-between text-xs text-white/70">
          <span className="tracking-widest uppercase" data-testid="header-tagline">Building Trust · Rising Higher</span>
          <div className="flex items-center gap-6">
            <a
              href={SITE.whatsappHref}
              target="_blank" rel="noreferrer"
              data-testid="header-whatsapp-link"
              className="flex items-center gap-2 hover:text-[#C5A059] transition-colors"
            >
              <MessageCircle size={14} /> Chat with us
            </a>
            <span className="opacity-60">{SITE.domain}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 md:py-4 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={SITE.logo} alt="SkyRise Estate & Construction" className="h-12 md:h-14 w-auto" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-white text-xl md:text-[22px] font-semibold tracking-tight">SkyRise</span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">Estate & Construction</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => `nav-link text-[15px] ${isActive ? "active" : ""}`}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.telHref}
            data-testid="header-phone-link"
            className="hidden md:flex items-center gap-3 group"
            aria-label={`Call ${SITE.phone}`}
          >
            <span className="w-10 h-10 grid place-items-center border border-[#C5A059]/70 group-hover:bg-[#C5A059] transition-colors">
              <Phone size={16} className="text-[#C5A059] group-hover:text-white" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">Call Now</span>
              <span className="text-white font-medium tracking-wide text-[15px]">{SITE.phone}</span>
            </span>
          </a>

          <a
            href={SITE.telHref}
            data-testid="header-cta-quote"
            className="hidden md:inline-flex btn-gold text-sm"
          >
            Get a Quote
          </a>

          <button
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 grid place-items-center border border-white/20 text-white"
            onClick={() => setOpen(v => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0B162C] border-t border-white/10" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => `text-lg tracking-wide ${isActive ? "text-[#C5A059]" : "text-white/85"}`}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
              <a href={SITE.telHref} className="text-white flex items-center gap-3" data-testid="mobile-phone-link">
                <Phone size={16} className="text-[#C5A059]" /> {SITE.phone}
              </a>
              <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" className="btn-gold justify-center text-sm" data-testid="mobile-whatsapp-cta">
                <MessageCircle size={16}/> Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
